<template>
  <div class="list-page">
    <!-- 검색 -->
    <div class="search-area">
      <div class="search-box">
        <svg class="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="현장명, 지역으로 검색"
          class="search-input"
        />
        <button v-if="searchQuery" @click="searchQuery = ''" class="search-clear">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 모바일 필터 (스크롤) -->
    <div class="filter-area mobile-only">
      <div class="filter-row">
        <button
          v-for="region in regionFilters"
          :key="region.value"
          :class="['filter-chip', activeRegion === region.value ? 'active' : '']"
          @click="activeRegion = region.value"
        >
          {{ region.label }}
        </button>
      </div>
      <div class="filter-row">
        <button
          v-for="type in typeFilters"
          :key="type.value"
          :class="['filter-chip small', activeType === type.value ? 'active' : '']"
          @click="activeType = type.value"
        >
          {{ type.label }}
        </button>
        <div class="filter-divider"></div>
        <button
          v-for="theme in themeFilters"
          :key="theme.value"
          :class="['filter-chip small', activeTheme === theme.value ? 'active' : '']"
          @click="activeTheme = theme.value"
        >
          {{ theme.label }}
        </button>
      </div>
    </div>

    <!-- 웹 필터 (버튼 + 드롭다운) -->
    <div class="filter-area desktop-only">
      <div class="filter-bar">
        <!-- 지역 드롭다운 -->
        <div class="filter-dropdown">
          <button class="dropdown-btn" @click="toggleDropdown('region')">
            <span>{{ activeRegionLabel }}</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <div v-if="openDropdown === 'region'" class="dropdown-menu">
            <button
              v-for="region in regionFilters"
              :key="region.value"
              :class="['dropdown-item', activeRegion === region.value ? 'active' : '']"
              @click="selectFilter('region', region.value)"
            >
              {{ region.label }}
            </button>
          </div>
        </div>

        <!-- 타입 드롭다운 -->
        <div class="filter-dropdown">
          <button class="dropdown-btn" @click="toggleDropdown('type')">
            <span>{{ activeTypeLabel }}</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <div v-if="openDropdown === 'type'" class="dropdown-menu">
            <button
              v-for="type in typeFilters"
              :key="type.value"
              :class="['dropdown-item', activeType === type.value ? 'active' : '']"
              @click="selectFilter('type', type.value)"
            >
              {{ type.label }}
            </button>
          </div>
        </div>

        <!-- 테마 드롭다운 -->
        <div class="filter-dropdown">
          <button class="dropdown-btn" @click="toggleDropdown('theme')">
            <span>{{ activeThemeLabel }}</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
          <div v-if="openDropdown === 'theme'" class="dropdown-menu">
            <button
              v-for="theme in themeFilters"
              :key="theme.value"
              :class="['dropdown-item', activeTheme === theme.value ? 'active' : '']"
              @click="selectFilter('theme', theme.value)"
            >
              {{ theme.label }}
            </button>
          </div>
        </div>

        <!-- 초기화 -->
        <button v-if="hasActiveFilter" class="reset-btn" @click="resetFilters">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
          초기화
        </button>
      </div>
    </div>

    <!-- 결과 정보 -->
    <div class="result-bar">
      <span class="result-count">{{ filteredProperties.length }}개 현장</span>
      <button class="sort-btn" @click="toggleSort">
        {{ sortLabel }}
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>
    </div>

    <!-- 현장 목록 -->
    <div class="property-list">
      <!-- 리스트 광고 영역 (최대 5개) - 인앱 광고 스타일 -->
      <div v-if="listAds.length > 0" class="ad-section">
        <router-link
          v-for="ad in listAds"
          :key="ad.id"
          :to="`/p/${ad.campaignCode}`"
          class="ad-banner-item"
          @click="handleAdClick(ad.id)"
        >
          <div
            class="ad-banner-bg"
            :style="{
              backgroundImage: ad.imageUrl
                ? `linear-gradient(105deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%), url('${ad.imageUrl}')`
                : 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
            }"
          ></div>
          <div class="ad-banner-content">
            <div class="ad-banner-top">
              <span v-if="ad.region" class="ad-region-tag">{{ ad.region }}</span>
              <span class="ad-badge-tag">AD</span>
            </div>
            <div class="ad-banner-bottom">
              <h3 class="ad-banner-title">{{ ad.title || ad.propertyName }}</h3>
              <p v-if="ad.subtitle" class="ad-banner-subtitle">{{ ad.subtitle }}</p>
              <div class="ad-banner-cta">
                <span>자세히 보기</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </router-link>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="state-box">
        <div class="spinner"></div>
        <p>현장을 불러오는 중...</p>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredProperties.length === 0" class="state-box">
        <div class="empty-icon">
          <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
          </svg>
        </div>
        <p class="empty-title">검색 결과가 없습니다</p>
        <p class="empty-sub">다른 필터를 선택해보세요</p>
      </div>

      <!-- 현장 리스트 -->
      <router-link
        v-else
        v-for="property in filteredProperties"
        :key="property.id"
        :to="`/properties/${property.id}`"
        class="property-item"
      >
        <div class="item-main">
          <div class="item-name-row">
            <h3 class="item-name">{{ property.name }}</h3>
            <span v-if="hasPartner(property.id)" class="consult-badge">
              <span class="consult-dot"></span>
              지금 상담 가능
            </span>
          </div>
          <p class="item-address">{{ property.address }}</p>
        </div>

        <div class="item-info">
          <div class="info-col">
            <span class="info-label">초기자금</span>
            <span class="info-value primary">{{ property.initialFund || '문의' }}</span>
          </div>
          <div class="info-col">
            <span class="info-label">세대수</span>
            <span class="info-value">{{ property.totalUnits?.toLocaleString() || '-' }}세대</span>
          </div>
          <div class="info-col">
            <span class="info-label">입주</span>
            <span class="info-value">{{ formatMoveIn(property.moveInDate) }}</span>
          </div>
        </div>

        <div class="item-tags">
          <span v-if="property.propertyType" class="tag">{{ getTypeLabel(property.propertyType) }}</span>
          <span v-for="theme in (property.themes || []).slice(0, 2)" :key="theme" class="tag">{{ getThemeLabel(theme) }}</span>
        </div>

        <div class="item-arrow">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </div>
      </router-link>
    </div>

    <!-- 하단 여백 -->
    <div class="bottom-space"></div>

    <!-- 드롭다운 백드롭 -->
    <div v-if="openDropdown" class="dropdown-backdrop" @click="openDropdown = null"></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useListAd } from '@/composables/main/useListAd'

export default {
  name: 'PropertyListView',
  setup() {
    const route = useRoute()
    const loading = ref(true)
    const properties = ref([])
    const searchQuery = ref('')
    const listAds = ref([])
    const propertiesWithPartners = ref(new Set()) // 상담사가 있는 현장 ID Set

    // 리스트 광고 composable
    const { getActiveListAds, incrementClick, incrementImpression } = useListAd()

    // 현장에 상담사가 있는지 확인
    const hasPartner = (propertyId) => {
      return propertiesWithPartners.value.has(propertyId)
    }
    const activeRegion = ref('all')
    const activeType = ref('all')
    const activeTheme = ref('all')
    const sortOrder = ref('latest')
    const openDropdown = ref(null)

    // 지역 필터 (regionMain 값과 매칭)
    const regionFilters = [
      { label: '전체', value: 'all' },
      { label: '서울', value: '서울' },
      { label: '경기', value: '경기' },
      { label: '인천', value: '인천' },
      { label: '부산', value: '부산' },
      { label: '대구', value: '대구' },
      { label: '대전', value: '대전' },
      { label: '광주', value: '광주' },
      { label: '울산', value: '울산' },
      { label: '세종', value: '세종' },
      { label: '강원', value: '강원' },
      { label: '충북', value: '충북' },
      { label: '충남', value: '충남' },
      { label: '전북', value: '전북' },
      { label: '전남', value: '전남' },
      { label: '경북', value: '경북' },
      { label: '경남', value: '경남' },
      { label: '제주', value: '제주' }
    ]

    const typeFilters = [
      { label: '전체', value: 'all' },
      { label: '아파트', value: 'apartment' },
      { label: '오피스텔', value: 'officetel' },
      { label: '빌라', value: 'villa' },
      { label: '상가', value: 'commercial' }
    ]

    const themeFilters = [
      { label: '전체', value: 'all' },
      { label: '실투자 3천만', value: 'money' },
      { label: '1군 브랜드', value: 'brand' },
      { label: '초역세권', value: 'subway' },
      { label: '안심 보장제', value: 'safe' },
      { label: '명문 학세권', value: 'school' },
      { label: '힐링 숲세권', value: 'forest' },
      { label: '직주근접', value: 'job' },
      { label: '몰세권', value: 'mall' }
    ]

    const activeRegionLabel = computed(() => {
      const found = regionFilters.find(r => r.value === activeRegion.value)
      return found ? (activeRegion.value === 'all' ? '지역' : found.label) : '지역'
    })

    const activeTypeLabel = computed(() => {
      const found = typeFilters.find(t => t.value === activeType.value)
      return found ? (activeType.value === 'all' ? '유형' : found.label) : '유형'
    })

    const activeThemeLabel = computed(() => {
      const found = themeFilters.find(t => t.value === activeTheme.value)
      return found ? (activeTheme.value === 'all' ? '테마' : found.label) : '테마'
    })

    const hasActiveFilter = computed(() => {
      return activeRegion.value !== 'all' || activeType.value !== 'all' || activeTheme.value !== 'all'
    })

    const sortLabel = computed(() => {
      const labels = { latest: '최신순', price: '가격순', units: '세대순' }
      return labels[sortOrder.value]
    })

    const toggleDropdown = (type) => {
      openDropdown.value = openDropdown.value === type ? null : type
    }

    const selectFilter = (type, value) => {
      if (type === 'region') activeRegion.value = value
      else if (type === 'type') activeType.value = value
      else if (type === 'theme') activeTheme.value = value
      openDropdown.value = null
    }

    const resetFilters = () => {
      activeRegion.value = 'all'
      activeType.value = 'all'
      activeTheme.value = 'all'
    }

    const toggleSort = () => {
      const orders = ['latest', 'price', 'units']
      const idx = orders.indexOf(sortOrder.value)
      sortOrder.value = orders[(idx + 1) % orders.length]
    }

    const filteredProperties = computed(() => {
      let result = properties.value

      // 검색
      if (searchQuery.value) {
        const q = searchQuery.value.toLowerCase()
        result = result.filter(p =>
          p.name?.toLowerCase().includes(q) ||
          p.address?.toLowerCase().includes(q)
        )
      }

      // 지역 필터 (regionMain 필드 사용)
      if (activeRegion.value !== 'all') {
        result = result.filter(p => p.regionMain === activeRegion.value)
      }

      // 타입 필터
      if (activeType.value !== 'all') {
        result = result.filter(p => p.propertyType === activeType.value)
      }

      // 테마 필터
      if (activeTheme.value !== 'all') {
        result = result.filter(p => p.themes?.includes(activeTheme.value))
      }

      // 정렬
      if (sortOrder.value === 'units') {
        result = [...result].sort((a, b) => (b.totalUnits || 0) - (a.totalUnits || 0))
      } else if (sortOrder.value === 'price') {
        result = [...result].sort((a, b) => {
          const aPrice = parsePrice(a.initialFund)
          const bPrice = parsePrice(b.initialFund)
          return aPrice - bPrice
        })
      }

      return result
    })

    const parsePrice = (str) => {
      if (!str) return 999999
      const num = str.replace(/[^0-9]/g, '')
      return parseInt(num) || 999999
    }

    const getTypeLabel = (t) => {
      const labels = { apartment: '아파트', officetel: '오피스텔', villa: '빌라', commercial: '상가', townhouse: '타운하우스' }
      return labels[t] || t
    }

    const getThemeLabel = (t) => {
      const labels = {
        'money': '실투자 3천만', 'brand': '1군 브랜드', 'subway': '초역세권',
        'safe': '안심 보장제', 'school': '명문 학세권', 'forest': '힐링 숲세권',
        'job': '직주근접', 'mall': '몰세권'
      }
      return labels[t] || t
    }

    const formatMoveIn = (d) => {
      if (!d) return '-'
      const [y, m] = d.split('-')
      return `${y}.${m}`
    }

    const loadProperties = async () => {
      try {
        loading.value = true
        const snapshot = await getDocs(collection(db, 'properties'))
        properties.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (error) {
        console.error('현장 로드 실패:', error)
      } finally {
        loading.value = false
      }
    }

    // 상담사가 등록된 현장 목록 로드
    const loadPropertiesWithPartners = async () => {
      try {
        const q = query(
          collection(db, 'propertyPartners'),
          where('status', '==', 'active')
        )
        const snapshot = await getDocs(q)
        const propertyIds = new Set()
        snapshot.docs.forEach(doc => {
          const data = doc.data()
          if (data.propertyId) {
            propertyIds.add(data.propertyId)
          }
        })
        propertiesWithPartners.value = propertyIds
      } catch (error) {
        console.error('상담사 현장 로드 실패:', error)
      }
    }

    // 리스트 광고 로드
    const loadListAds = async () => {
      try {
        const ads = await getActiveListAds()
        listAds.value = ads

        // 노출 수 증가
        for (const ad of ads) {
          incrementImpression(ad.id)
        }
      } catch (error) {
        console.error('리스트 광고 로드 실패:', error)
      }
    }

    // 광고 클릭 핸들러
    const handleAdClick = (adId) => {
      incrementClick(adId)
    }

    // URL 쿼리 파라미터로 필터 초기화
    const initFiltersFromQuery = () => {
      if (route.query.theme) {
        activeTheme.value = route.query.theme
      }
      if (route.query.region) {
        activeRegion.value = route.query.region
      }
      if (route.query.type) {
        activeType.value = route.query.type
      }
    }

    // 라우트 변경 감지
    watch(() => route.query, () => {
      initFiltersFromQuery()
    })

    onMounted(() => {
      initFiltersFromQuery()
      loadProperties()
      loadListAds()
      loadPropertiesWithPartners()
    })

    return {
      loading,
      searchQuery,
      activeRegion,
      activeType,
      activeTheme,
      regionFilters,
      typeFilters,
      themeFilters,
      activeRegionLabel,
      activeTypeLabel,
      activeThemeLabel,
      hasActiveFilter,
      sortOrder,
      sortLabel,
      openDropdown,
      toggleDropdown,
      selectFilter,
      resetFilters,
      toggleSort,
      filteredProperties,
      getTypeLabel,
      getThemeLabel,
      formatMoveIn,
      listAds,
      handleAdClick,
      hasPartner
    }
  }
}
</script>

<style scoped>
.list-page {
  min-height: 100vh;
  background: #ffffff;
}

/* 반응형 전환 */
.mobile-only { display: block; }
.desktop-only { display: none; }
@media (min-width: 769px) {
  .mobile-only { display: none; }
  .desktop-only { display: block; }
}

/* 검색 */
.search-area {
  position: sticky;
  top: 60px;
  z-index: 30;
  background: white;
  padding: 12px 16px;
}
@media (max-width: 768px) {
  .search-area { top: 0; }
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f1f5f9;
  border-radius: 12px;
  padding: 10px 14px;
  max-width: 600px;
  margin: 0 auto;
}

.search-icon {
  width: 18px;
  height: 18px;
  color: #94a3b8;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1e293b;
}
.search-input::placeholder { color: #94a3b8; }

.search-clear {
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  background: #e2e8f0;
  border-radius: 50%;
}

/* 모바일 필터 */
.filter-area.mobile-only {
  background: white;
  border-bottom: 1px solid #f1f5f9;
  padding: 0 16px;
}

.filter-row {
  display: flex;
  gap: 6px;
  padding: 10px 0;
  overflow-x: auto;
  max-width: 512px;
  margin: 0 auto;
}
.filter-row::-webkit-scrollbar { display: none; }
.filter-row:first-child { border-bottom: 1px solid #f1f5f9; }

.filter-chip {
  padding: 7px 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  white-space: nowrap;
  transition: all 0.15s;
}
.filter-chip.small {
  padding: 5px 10px;
  font-size: 12px;
}
.filter-chip.active {
  background: #1e293b;
  border-color: #1e293b;
  color: white;
}

.filter-divider {
  width: 1px;
  background: #e2e8f0;
  margin: 0 4px;
  flex-shrink: 0;
}

/* 웹 필터 (드롭다운) */
.filter-area.desktop-only {
  background: white;
  border-bottom: 1px solid #f1f5f9;
  padding: 12px 16px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 600px;
  margin: 0 auto;
}

.filter-dropdown {
  position: relative;
}

.dropdown-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}
.dropdown-btn:hover {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 140px;
  max-height: 280px;
  overflow-y: auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  z-index: 50;
  padding: 6px;
}

.dropdown-item {
  display: block;
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  font-size: 13px;
  color: #475569;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;
}
.dropdown-item:hover {
  background: #f1f5f9;
}
.dropdown-item.active {
  background: #2563eb;
  color: white;
}

.reset-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  background: none;
  border: none;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
}
.reset-btn:hover {
  color: #475569;
}

.dropdown-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}

/* 결과 바 */
.result-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  max-width: 600px;
  margin: 0 auto;
}

.result-count {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.sort-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  background: none;
  border: none;
}

/* 현장 목록 */
.property-list {
  padding: 0 16px 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* 상태 박스 */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.spinner {
  width: 28px;
  height: 28px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.state-box p {
  font-size: 14px;
  color: #94a3b8;
}

.empty-icon {
  width: 56px;
  height: 56px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
  color: #cbd5e1;
}

.empty-title {
  font-size: 15px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.empty-sub {
  font-size: 13px;
  color: #94a3b8;
}

/* 현장 아이템 */
.property-item {
  display: block;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 10px;
  position: relative;
  transition: all 0.15s;
}
.property-item:hover {
  background: #fafbfc;
}
.property-item:active {
  transform: scale(0.99);
}

.item-main {
  margin-bottom: 12px;
  padding-right: 24px;
}

.item-name {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 3px;
  line-height: 1.3;
}

.item-address {
  font-size: 13px;
  color: #64748b;
}

.item-info {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
}

.info-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-label {
  font-size: 11px;
  color: #94a3b8;
}

.info-value {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}
.info-value.primary {
  color: #2563eb;
}

.item-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.tag {
  padding: 3px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  font-size: 11px;
  color: #475569;
}

.item-arrow {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  color: #cbd5e1;
}

/* 하단 여백 */
.bottom-space {
  height: 80px;
}

/* 리스트 광고 섹션 - 인앱 광고 스타일 */
.ad-section {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.ad-banner-item {
  display: block;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 12px;
  height: 140px;
  transition: all 0.2s;
}
.ad-banner-item:last-child {
  margin-bottom: 0;
}
.ad-banner-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}
.ad-banner-item:active {
  transform: scale(0.98);
}

.ad-banner-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}

.ad-banner-content {
  position: relative;
  height: 100%;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  color: white;
}

.ad-banner-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.ad-region-tag {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.ad-badge-tag {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 6px;
  background: #f97316;
}

.ad-banner-bottom {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ad-banner-title {
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
}

.ad-banner-subtitle {
  font-size: 13px;
  opacity: 0.9;
}

.ad-banner-cta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  opacity: 0.9;
}

/* 지금 상담 가능 배지 */
.item-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.consult-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #dcfce7;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #16a34a;
  white-space: nowrap;
}

.consult-dot {
  width: 6px;
  height: 6px;
  background: #22c55e;
  border-radius: 50%;
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(0.85);
  }
}
</style>
